import { Component } from '@theme/component';

class DeluluCollectionTabs extends Component {
  #abort;
  #saved = new Map();
  #active = '1';
  #page = 0;
  #mobile;
  #animation;
  #resizeObserver;
  #width = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const nav = this.querySelector('.delulu-edit__tabs');
    const panel = this.querySelector('[data-edit-panel]');
    const more = this.querySelector('[data-edit-more]');
    const heading = this.querySelector('.delulu-edit__heading [data-edit-heading]');
    if (!nav || !panel) return;
    this.#mobile = window.matchMedia('(max-width: 749px)');
    this.#mobile.addEventListener('change', () => {
      this.#page = 0;
      panel.scrollLeft = 0;
      this.#renderPage();
    }, { signal });
    this.querySelectorAll('template[data-edit-content]').forEach((template) => {
      this.#saved.set(template.dataset.editContent, template.content);
      template.remove();
    });
    nav.setAttribute('role', 'tablist');
    panel.setAttribute('role', 'tabpanel');
    panel.tabIndex = 0;
    const tabs = () => [...nav.querySelectorAll('[data-edit-tab]')];
    const sync = () => tabs().forEach((tab) => {
      const selected = tab.dataset.editTab === this.#active;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('aria-controls', panel.id);
      tab.toggleAttribute('aria-current', selected);
      tab.tabIndex = selected ? 0 : -1;
      if (selected) {
        panel.setAttribute('aria-labelledby', tab.id);
        if (heading && tab.dataset.editHeading) heading.textContent = tab.dataset.editHeading;
        if (more) {
          more.href = tab.href;
          more.textContent = tab.dataset.editLabel;
        }
      }
    });
    const select = (tab) => {
      this.#animation?.cancel();
      const key = tab.dataset.editTab;
      if (key !== this.#active) {
        const next = this.#saved.get(key);
        if (!next) return;
        const previous = document.createDocumentFragment();
        previous.append(...panel.childNodes);
        this.#saved.set(this.#active, previous);
        panel.append(next);
        this.#active = key;
        this.#page = 0;
        panel.scrollLeft = 0;
      }
      sync();
      this.#renderPage(true);
    };
    sync();
    this.#renderPage();
    this.setAttribute('data-paging-ready', '');
    this.#width = panel.clientWidth;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === this.#width) return;
      this.#width = entry.contentRect.width;
      this.#renderPage();
    });
    this.#resizeObserver.observe(panel);
    this.querySelectorAll('[data-edit-previous], [data-edit-next]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        const keyboardFocus = button.matches(':focus-visible');
        this.#page += button.hasAttribute('data-edit-next') ? 1 : -1;
        this.#renderPage(true, true);
        if (keyboardFocus && button.disabled) {
          this.querySelector('[data-edit-previous]:not(:disabled), [data-edit-next]:not(:disabled)')?.focus({ preventScroll: true });
        }
      }, { signal });
    });
    nav.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-edit-tab]');
      if (!tab || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      select(tab);
    }, { signal });
    nav.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[data-edit-tab]');
      if (!tab) return;
      const items = tabs();
      let index = items.indexOf(tab);
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowRight') index += rtl ? -1 : 1;
      else if (event.key === 'ArrowLeft') index += rtl ? 1 : -1;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = items.length - 1;
      else return;
      event.preventDefault();
      const next = items[(index + items.length) % items.length];
      select(next);
      next.focus({ preventScroll: true });
      next.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }, { signal });
  }

  #renderPage(announce = false, animate = false) {
    const grid = this.querySelector('[data-edit-panel] .delulu-edit__grid');
    if (!grid) return;
    const from = getComputedStyle(grid).transform;
    this.#animation?.cancel();
    const items = [...grid.querySelectorAll(':scope > .resource-list__item')];
    const total = Math.min(items.length, 10);
    const pageSize = this.#mobile.matches ? 2 : 5;
    const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    this.#page = Math.max(0, Math.min(this.#page, lastPage));
    // Clamp the final page to the last full row instead of exposing empty columns.
    const start = Math.min(this.#page * pageSize, Math.max(0, total - pageSize));
    items.forEach((item, index) => {
      const visible = index < total && (this.#mobile.matches || (index >= start && index < start + pageSize));
      item.hidden = index >= total;
      item.inert = !visible;
      item.setAttribute('aria-hidden', String(!visible));
      // Have the next page's primary images ready before its slide begins.
      if (index < total) item.querySelector('slideshow-slide img')?.setAttribute('loading', 'eager');
    });
    const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    const direction = getComputedStyle(grid).direction === 'rtl' ? 1 : -1;
    const transform = this.#mobile.matches ? 'none' : `translateX(${direction * start * (grid.clientWidth + gap) / pageSize}px)`;
    grid.style.transform = transform;
    if (animate && !this.#mobile.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#animation = grid.animate([{ transform: from }, { transform }], {
        duration: 420,
        easing: 'cubic-bezier(.22,.68,0,1)',
      });
    }
    const previous = this.querySelector('[data-edit-previous]');
    const next = this.querySelector('[data-edit-next]');
    if (previous) {
      previous.hidden = this.#mobile.matches || total <= pageSize;
      previous.disabled = this.#page === 0;
    }
    if (next) {
      next.hidden = this.#mobile.matches || total <= pageSize;
      next.disabled = this.#page === lastPage;
    }
    const status = this.querySelector('[data-edit-status]');
    if (status && announce && !this.#mobile.matches) {
      status.textContent = status.dataset.statusTemplate
        .replace('[index]', String(this.#page + 1))
        .replace('[length]', String(lastPage + 1));
    } else if (status) {
      status.textContent = '';
    }
  }

  disconnectedCallback() {
    this.#animation?.cancel();
    this.#resizeObserver?.disconnect();
    this.#abort?.abort();
    super.disconnectedCallback();
  }
}
if (!customElements.get('delulu-collection-tabs')) customElements.define('delulu-collection-tabs', DeluluCollectionTabs);
